import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Cpu, Server, Shield, Zap, ArrowRight, Check } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const gpuConfig = {
  h200: {
    name: "NVIDIA H200 AI Cloud",
    tagline: "Unmatched performance for LLM Training and AI.",
    desc: "Train on H200. The ultimate enterprise AI workload infrastructure.",
    color: "#BC13FE",
    icon: Zap,
    image: "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?q=80&w=800&auto=format&fit=crop", // placeholder
    features: ["141GB HBM3e Memory", "4.8 TB/s Memory Bandwidth", "Next-Gen Hopper Architecture", "Enterprise Security"],
  },
  a6000: {
    name: "RTX A6000 Pro Blackwell",
    tagline: "Scale your AI Inference efficiently.",
    desc: "Shift your production workloads to cost-efficient A6000 infrastructure.",
    color: "#00FF94",
    icon: Cpu,
    image: "https://images.unsplash.com/photo-1591488320449-011701bb6704?q=80&w=800&auto=format&fit=crop", // placeholder
    features: ["96GB GDDR7 VRAM", "DDR5 Memory", "Gen5 NVMe Storage", "Instant Deployment"],
  },
  a100: {
    name: "A100 GPU",
    tagline: "Power. Performance. Now Affordable.",
    desc: "High Performance AI Compute at up to 70% Lower Cost than hyperscalers.",
    color: "#00F3FF",
    icon: Server,
    image: "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=800&auto=format&fit=crop", // placeholder
    features: ["40GB SXM4 Memory", "AMD EPYC 7742 (64 Cores)", "1 TB DDR4 ECC RAM", "200G Ready Network"],
  },
  v100: {
    name: "V100 NVIDIA",
    tagline: "Unleash GPU Performance at a Great Price.",
    desc: "The perfect entry point for AI, ML, and HPC workloads.",
    color: "#ffcc00",
    icon: Server,
    image: "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?q=80&w=800&auto=format&fit=crop", // placeholder
    features: ["32GB V100 NVIDIA", "Massive Parallelism", "High Memory Bandwidth", "Enterprise Reliability"],
  }
};

export default function GpuDetail() {
  const { card } = useParams();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const config = gpuConfig[card?.toLowerCase()] || gpuConfig.a100;

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/plans/category/gpu`);
        // Try to filter plans that match this card
        const filtered = res.data.filter(p => p.name.toLowerCase().includes(card.toLowerCase()) || p.specs?.gpu?.toLowerCase().includes(card.toLowerCase()));
        setPlans(filtered.length > 0 ? filtered : res.data); // Fallback to all GPU plans if exact match not found
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
    window.scrollTo(0, 0);
  }, [card]);

  return (
    <div className="page-enter pt-24 pb-16">
      {/* Hero Banner */}
      <section className="relative py-20 overflow-hidden"
        style={{ background: `radial-gradient(ellipse at 50% 0%, ${config.color}20 0%, transparent 60%), #030305` }}>
        <div className="animated-grid absolute inset-0 opacity-30" />
        <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center relative z-10">
          <div>
            <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full text-xs font-rajdhani font-700 tracking-widest"
              style={{ background: `${config.color}15`, border: `1px solid ${config.color}40`, color: config.color }}>
              <config.icon size={12} /> {config.tagline.toUpperCase()}
            </div>
            <h1 className="font-orbitron font-900 text-4xl lg:text-5xl text-white mb-6 leading-tight">
              {config.name}
            </h1>
            <p className="font-outfit text-lg text-gray-300 mb-8 leading-relaxed">
              {config.desc}
            </p>
            <div className="flex flex-col gap-4 mb-8">
              {config.features.map(f => (
                <div key={f} className="flex items-center gap-3">
                  <Check size={16} color={config.color} className="shrink-0" />
                  <span className="font-outfit text-gray-300">{f}</span>
                </div>
              ))}
            </div>
            <Link to="/contact">
              <button className="btn-primary px-8 py-3 flex items-center gap-2 font-700" style={{ background: config.color, color: "#030305", boxShadow: `0 0 20px ${config.color}60` }}>
                REQUEST QUOTE <ArrowRight size={16} />
              </button>
            </Link>
          </div>
          
          <div className="relative">
            <div className="absolute inset-0 rounded-2xl blur-3xl opacity-20" style={{ background: config.color }} />
            <img src={config.image} alt={config.name} className="relative z-10 rounded-2xl border w-full object-cover shadow-2xl" style={{ borderColor: `${config.color}40`, height: "400px" }} />
          </div>
        </div>
      </section>

      {/* Available Plans for this GPU */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-orbitron font-700 text-3xl text-white">Available <span style={{ color: config.color }}>Deployments</span></h2>
            <p className="font-outfit text-gray-400 mt-2">Choose the configuration that fits your workload.</p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-pulse-glow w-12 h-12 rounded-xl" style={{ background: `${config.color}20`, border: `1px solid ${config.color}50` }} />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 justify-center">
              {plans.map((plan) => (
                <div key={plan.id} className="glass rounded-2xl p-6 transition-all hover:-translate-y-2" style={{ border: `1px solid ${config.color}30` }}>
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="font-orbitron font-700 text-xl text-white">{plan.name}</h3>
                    {plan.badge && <span className="text-[10px] font-rajdhani font-700 px-2 py-1 rounded" style={{ background: `${config.color}20`, color: config.color }}>{plan.badge}</span>}
                  </div>
                  <div className="mb-6">
                    <div className="font-orbitron font-900 text-3xl text-white">
                      ₹{plan.price.toLocaleString("en-IN")}
                    </div>
                    <div className="font-outfit text-sm text-gray-500">/{plan.price_period} + tax</div>
                  </div>
                  
                  <div className="space-y-3 mb-8">
                    {plan.features?.slice(0, 5).map(f => (
                      <div key={f} className="flex items-start gap-2 font-outfit text-sm text-gray-300">
                        <Check size={14} color={config.color} className="shrink-0 mt-0.5" />
                        {f}
                      </div>
                    ))}
                  </div>
                  
                  <Link to="/contact" className="block w-full">
                    <button className="w-full py-2.5 rounded-lg font-rajdhani font-700 text-sm transition-all" style={{ border: `1px solid ${config.color}`, color: config.color, background: `${config.color}10` }}>
                      DEPLOY NOW
                    </button>
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
